<template>
  <div class="core-panel venue-snapshot-panel">
    <div class="row" style="margin-bottom: 0">
      <div class="col s12">
        <p class="grey-text text-darken-1 venue-snapshot-line">
          <strong>Venue:</strong>

          <template v-if="showVenueDropdown">
            <select
              v-model="selectedVenue"
              class="browser-default venue-snapshot-select"
              :disabled="loadingVenues"
            >
              <option value="" disabled>
                {{ loadingVenues ? "Loading venues..." : "Select a venue" }}
              </option>

              <option v-for="v in venues" :key="v.id" :value="v.id">
                {{ v.label }}
              </option>
            </select>
          </template>

          <template v-else>
            <span v-if="venueId">
              {{ venueName || venueId }}
            </span>

            <span v-else class="red-text"> No venue loaded </span>
          </template>

          <span v-if="venuesError" class="red-text">
            {{ venuesError }}
          </span>

          <template v-if="!isSocialMediaUser">
            &nbsp;|&nbsp;

            <strong> Trading date: </strong>

            <span v-if="tradingDate">
              {{ displayDate(tradingDate) }}
            </span>

            <span v-else class="red-text"> Not set </span>

            &nbsp;|&nbsp;

            <strong> Week ending: </strong>

            <span v-if="weekEnding">
              {{ displayDate(weekEnding) }}
            </span>

            <span v-else class="red-text"> Not set </span>

            <span class="venue-snapshot-spacer"></span>

            <!--
            PRINT WEEKLY PACK DISABLED
            -->
          </template>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import { useRouter } from "vue-router";

import { onAuthStateChanged } from "firebase/auth";

import { doc, getDoc, onSnapshot } from "firebase/firestore";

import { auth, db } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { useDayLock } from "@/scripts/useScripts/useDayLock";

import { listVenuesForSnapshotDropdown } from "@/scripts/sqlApiHelper/venuesApi";

/* global M */

const router = useRouter();

const {
  venueId,
  isOperations,
  userType,
  isMultiVenue,
  allowedVenueIds,
} = useVenueContext();

const { tradingDate, weekEnding, start } = useDayLock(venueId);

start();

const profileUserType = ref("");
const profileIsSocialMedia = ref(false);
const profileVenueAccessIds = ref([]);

let unsubAuth = null;
let unsubProfile = null;

function normaliseAccessIds(data) {
  const ids = new Set();

  if (Array.isArray(data?.venue_ids)) {
    data.venue_ids.forEach((id) => {
      const clean = asStr(id);

      if (clean) {
        ids.add(clean);
      }
    });
  }

  if (Array.isArray(data?.venueIds)) {
    data.venueIds.forEach((id) => {
      const clean = asStr(id);

      if (clean) {
        ids.add(clean);
      }
    });
  }

  if (data?.venue_access && typeof data.venue_access === "object") {
    Object.entries(data.venue_access).forEach(([id, allowed]) => {
      if (allowed === true && asStr(id)) {
        ids.add(asStr(id));
      }
    });
  }

  if (data?.venueAccess && typeof data.venueAccess === "object") {
    Object.entries(data.venueAccess).forEach(([id, allowed]) => {
      if (allowed === true && asStr(id)) {
        ids.add(asStr(id));
      }
    });
  }

  if (data?.venues && typeof data.venues === "object" && !Array.isArray(data.venues)) {
    Object.entries(data.venues).forEach(([id, allowed]) => {
      if (allowed === true && asStr(id)) {
        ids.add(asStr(id));
      }
    });
  }

  const singleVenueId = asStr(data?.venue_id || data?.venueId || data?.venue);

  if (singleVenueId) {
    ids.add(singleVenueId);
  }

  return Array.from(ids);
}

function applyUserProfile(data) {
  const cleanType = asStr(data?.user_type || data?.userType).toLowerCase();

  profileUserType.value = cleanType;

  profileIsSocialMedia.value =
    cleanType === "social_media" ||
    data?.isSocialMedia === true ||
    data?.is_social_media === true ||
    data?.social_media === true;

  profileVenueAccessIds.value = normaliseAccessIds(data || {});
}

function attachProfileListener(uid) {
  if (typeof unsubProfile === "function") {
    unsubProfile();
  }

  unsubProfile = null;

  profileUserType.value = "";
  profileIsSocialMedia.value = false;
  profileVenueAccessIds.value = [];

  if (!uid) {
    return;
  }

  unsubProfile = onSnapshot(
    doc(db, "users", uid),

    (snap) => {
      applyUserProfile(snap.exists() ? snap.data() || {} : {});

      refreshVenues();
    },

    (error) => {
      console.error("[VenueSnapshot] user profile listener failed:", error);

      profileUserType.value = "";
      profileIsSocialMedia.value = false;
      profileVenueAccessIds.value = [];
    }
  );
}

const venues = ref([]);
const loadingVenues = ref(false);
const venuesError = ref("");
const selectedVenue = ref("");

const isSocialMediaUser = computed(
  () =>
    profileIsSocialMedia.value ||
    String(userType.value || "")
      .trim()
      .toLowerCase() === "social_media"
);

const effectiveAllowedVenueIds = computed(() => {
  if (profileVenueAccessIds.value.length > 0) {
    return profileVenueAccessIds.value;
  }

  return Array.isArray(allowedVenueIds.value) ? allowedVenueIds.value : [];
});

const showVenueDropdown = computed(
  () =>
    !!isOperations.value ||
    isSocialMediaUser.value ||
    String(userType.value || "").toLowerCase() === "multi"
);

const activeVenueId = computed(() => asStr(venueId.value));

const venueName = ref("");
let unsubVenue = null;

function asStr(value) {
  return String(value ?? "").trim();
}

function displayDate(value) {
  const clean = asStr(value).slice(0, 10);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
    return clean;
  }

  const [yyyy, mm, dd] = clean.split("-");

  return `${dd}/${mm}/${yyyy}`;
}

function setActiveVenue(value) {
  const next = asStr(value);

  if (!next) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("ops-venue-changed", {
      detail: next,
    })
  );
}

function goToHolidayRequests() {
  const selectedVenueId = asStr(venueId.value);

  if (!selectedVenueId) {
    const message = "No venue selected.";

    console.warn("[VenueSnapshot] Cannot open Holiday Requests without venueId.");

    M?.toast?.({
      html: message,
    });

    return;
  }

  router.push({
    name: "HolidayRequests",

    params: {
      venueId: selectedVenueId,
    },
  });
}

async function refreshVenues() {
  if (!showVenueDropdown.value) {
    venues.value = [];
    selectedVenue.value = "";

    return;
  }

  loadingVenues.value = true;
  venuesError.value = "";

  try {
    const effectiveUserType = isSocialMediaUser.value ? "multi" : userType.value;

    const effectiveAllowed = effectiveAllowedVenueIds.value;

    const list = await listVenuesForSnapshotDropdown({
      isOperations: !!isOperations.value,

      userType: effectiveUserType,

      allowedVenueIds: effectiveAllowed,
    });

    if (isOperations.value) {
      venues.value = Array.isArray(list) ? list : [];
    } else if (isMultiVenue.value || isSocialMediaUser.value) {
      venues.value = (Array.isArray(list) ? list : []).filter((venue) =>
        effectiveAllowed.includes(venue.id)
      );
    } else {
      venues.value = Array.isArray(list) ? list : [];
    }

    const current = asStr(venueId.value);

    const match = venues.value.find((venue) => venue.id === current);

    selectedVenue.value = match ? match.id : venues.value[0]?.id || "";
  } catch (error) {
    venues.value = [];

    venuesError.value = error?.message || String(error);
  } finally {
    loadingVenues.value = false;
  }
}

watch(
  selectedVenue,

  (value, previousValue) => {
    if (!showVenueDropdown.value) {
      return;
    }

    const next = asStr(value);

    const old = asStr(previousValue);

    if (!next || next === old) {
      return;
    }

    setActiveVenue(next);
  }
);

watch(
  () => venueId.value,

  (id) => {
    if (typeof unsubVenue === "function") {
      unsubVenue();
    }

    unsubVenue = null;
    venueName.value = "";

    const cleanId = asStr(id);

    if (!cleanId) {
      return;
    }

    unsubVenue = onSnapshot(
      doc(db, "venues", cleanId),

      (snap) => {
        if (!snap.exists()) {
          venueName.value = "";

          return;
        }

        const data = snap.data() || {};

        venueName.value = asStr(
          data.venue_name ?? data.venueName ?? data.name ?? data.label ?? ""
        );
      },

      (error) => {
        console.error("[VenueSnapshot] venue lookup failed:", error);

        venueName.value = "";
      }
    );
  },

  {
    immediate: true,
  }
);

onMounted(async () => {
  unsubAuth = onAuthStateChanged(auth, async (user) => {
    attachProfileListener(user?.uid || "");

    if (!user?.uid) {
      await refreshVenues();

      return;
    }

    try {
      const snap = await getDoc(doc(db, "users", user.uid));

      applyUserProfile(snap.exists() ? snap.data() || {} : {});
    } catch (error) {
      console.error("[VenueSnapshot] initial user profile load failed:", error);
    }

    await refreshVenues();
  });

  if (showVenueDropdown.value) {
    await refreshVenues();
  }
});

watch(
  () => [
    showVenueDropdown.value,
    userType.value,
    isOperations.value,
    isMultiVenue.value,
    effectiveAllowedVenueIds.value.join("|"),
    isSocialMediaUser.value,
  ],

  async () => {
    if (showVenueDropdown.value) {
      await refreshVenues();
    }
  }
);

onUnmounted(() => {
  if (typeof unsubAuth === "function") {
    unsubAuth();
  }

  if (typeof unsubProfile === "function") {
    unsubProfile();
  }

  if (typeof unsubVenue === "function") {
    unsubVenue();
  }
});
</script>

<style scoped>
.venue-snapshot-panel {
  margin-top: 12px;
}

.venue-snapshot-line {
  margin: 0;

  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 8px;
}

.venue-snapshot-select {
  width: auto;

  height: 28px;
  line-height: 28px;

  padding: 0 8px;

  max-width: 320px;
}

.venue-snapshot-spacer {
  flex: 1 1 auto;
}

@media (max-width: 600px) {
  .venue-snapshot-line {
    align-items: flex-start;

    gap: 6px;

    line-height: 1.45;
  }

  .venue-snapshot-select {
    width: 100% !important;

    max-width: 100% !important;

    min-height: 36px;

    height: 36px !important;

    line-height: 36px !important;

    margin-top: 4px;
  }

  .venue-snapshot-spacer {
    display: none;
  }
}
</style>
